import cx from 'classnames';
import PropTypes from 'prop-types';
import {useEffect, useMemo, useRef} from 'react';
import {DetailsToggle} from '~/SignUpSheets/components/DetailsToggle/DetailsToggle';
import {resizeTextarea} from '~/SignUpSheets/utils/misc';
import {Button} from '~library/atoms/Button';
import {Input} from '~library/atoms/Input';
import {Typography} from '~library/atoms/Typography';
import {useIsLargishAndUp} from '~library/hooks/useMatchQuery';
import {ChevronIcon} from '~library/svg/ChevronIcon';

import './Sidebar.sass';

export const Sidebar = ({
  dayDescription = '',
  dateTitle = '',
  onDescriptionChange,
  onTitleChange,
  onToggleTimeSlots,
  sidebarInputs = [],
  allDay,
  uuid = '',
  expanded = false,
  onToggle,
  toolsMenu = null,
  hasSignups = false,
}) => {
  const sidebarInputIndex = sidebarInputs[0]?.props?.rowIndex;
  const parentQaId = sidebarInputs[0]?.props?.dataQaId;
  const descriptionRef = useRef(null);
  const dateTitleRef = useRef(null);
  const isDesktop = useIsLargishAndUp();

  // biome-ignore lint/correctness/useExhaustiveDependencies: existing error
  useEffect(() => {
    resizeTextarea(dateTitleRef.current);
  }, [dateTitleRef.current]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: existing error
  useEffect(() => {
    resizeTextarea(descriptionRef.current);
  }, [descriptionRef.current]);

  const secondaryInputs = useMemo(() => {
    if (expanded) {
      return (
        <div className="builder__slot-sidebar__secondary-inputs">
          <div className="builder__slot-sidebar__time-slot-needed">
            <Typography variant={isDesktop ? 'paragraph2' : 'paragraph3'} semibold color="asphalt">
              Include time windows
            </Typography>
            {/* <TimeWindowTooltip /> */} {/* TODO: This will be restored in a follow-up */}
            <Input
              type="toggle"
              checked={!allDay}
              onChange={(e) => onToggleTimeSlots(e.target.checked)}
              data-qa-id={`${parentQaId}-toggle-time-slots`}
              size="small"
            />
          </div>
          <DetailsToggle
            dataQaId={`${parentQaId}-toggle-details`}
            defaultOpen={Boolean(dateTitle || dayDescription)}
          >
            <Input
              inputClassName="builder__slot-sidebar__textarea"
              value={dateTitle}
              onChange={(e) => onTitleChange(e.target.value)}
              placeholder="Add date title"
              maxLength={50}
              type="textarea"
              data-qa-id={`${parentQaId}-slot-day-title-${sidebarInputIndex}`}
              onInput={() => resizeTextarea(dateTitleRef.current)}
              rows={1}
              ref={dateTitleRef}
              live
              variant={isDesktop ? 'paragraph1' : 'paragraph2'}
            />
            <Input
              inputClassName="builder__slot-sidebar__textarea"
              value={dayDescription}
              onChange={(e) => onDescriptionChange(e.target.value)}
              placeholder="Add date description"
              maxLength={200}
              type="textarea"
              data-qa-id={`${parentQaId}-slot-day-description-${sidebarInputIndex}`}
              onInput={() => resizeTextarea(descriptionRef.current)}
              rows={1}
              ref={descriptionRef}
              live
              variant={isDesktop ? 'paragraph1' : 'paragraph2'}
            />
          </DetailsToggle>
        </div>
      );
    }
    return null;
  }, [
    isDesktop,
    allDay,
    onToggleTimeSlots,
    parentQaId,
    dateTitle,
    dayDescription,
    onTitleChange,
    sidebarInputIndex,
    onDescriptionChange,
    expanded,
  ]);

  return (
    <>
      <div className={cx('builder__slot-sidebar__accordion', {expanded})}>
        <Button
          variant="transparent"
          onClick={() => onToggle?.(uuid, !expanded)}
          className={cx('builder__slot-sidebar__expand-collapse-toggle', {static: hasSignups})}
          data-qa-id={`group-expand-collapse-${uuid}`}
          ariaLabel={`${expanded ? 'Collapse' : 'Expand'} group`}
          type="button"
        >
          <ChevronIcon direction={expanded ? 'up' : 'down'} ratio={0.75} />
        </Button>
        <div className="builder__slot-sidebar">
          {sidebarInputs}
          {/* TODO: Make ONLY sidebarInputs sticky on MW */}
          {isDesktop ? secondaryInputs : null}
        </div>
        {toolsMenu}
      </div>
      {!isDesktop ? secondaryInputs : null}
    </>
  );
};

Sidebar.propTypes = {
  dayDescription: PropTypes.string,
  dateTitle: PropTypes.string,
  onDescriptionChange: PropTypes.func.isRequired,
  onTitleChange: PropTypes.func.isRequired,
  onToggleTimeSlots: PropTypes.func.isRequired,
  sidebarInputs: PropTypes.node,
  allDay: PropTypes.bool.isRequired,
  uuid: PropTypes.string,
  expanded: PropTypes.bool,
  onToggle: PropTypes.func,
};
